import React, { useState, useEffect } from 'react';
import { WorkReportFormNew } from './WorkReportFormNew';
import { WorkReportView } from './WorkReportView';
import './WorkReportsList.css';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import { WorkReport } from '../../types/catalogs';
import { useAuth } from '../../contexts/AuthContext';

interface WorkReportsListProps {
  projectId: number;
}

export const WorkReportsList = ({ projectId }: WorkReportsListProps) => {
  const { t } = useTranslation();
  const { user, token } = useAuth();
  const canManage = ['admin', 'project_manager'].includes(user?.role || '');
  const authHeaders: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};
  const [workReports, setWorkReports] = useState<WorkReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingReport, setEditingReport] = useState<WorkReport | null>(null);
  const [selectedReport, setSelectedReport] = useState<WorkReport | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showReportView, setShowReportView] = useState(false);

  useEffect(() => {
    fetchWorkReports();
  }, [projectId, token]);

  const fetchWorkReports = async () => {
    try {
      const backendUrl = sessionStorage.getItem('backendUrl') || 'http://localhost:1337';

      // Construir query con populate profundo para todas las relaciones
      const populateQuery = [
        'populate[project]=*',
        'populate[personalPropio][populate][employee]=*',
        'populate[maquinariaPropia][populate][machinery]=*',
        'populate[maquinariaAlquilada][populate][supplier]=*',
        'populate[temperaturaMezcla]=*',
        'populate[camionesFresando][populate][truck]=*',
        'populate[camionesObra][populate][truck]=*',
        'populate[materialesExtendido][populate][material]=*',
        'populate[images]=*',
        'populate[attachments]=*',
        'populate[createdBy]=*',
        'populate[approvedBy]=*'
      ].join('&');

      const response = await fetch(
        `${backendUrl}/api/work-reports?filters[project][id][$eq]=${projectId}&${populateQuery}&sort=fecha:desc`,
        { headers: authHeaders }
      );
      const data = await response.json();
      setWorkReports(data.data || []);
    } catch (error) {
      console.error('Error fetching work reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateReport = () => {
    setEditingReport(null);
    setShowForm(true);
  };

  const handleEditReport = (report: WorkReport) => {
    setEditingReport(report);
    setShowForm(true);
  };

  const handleDeleteReport = async (reportId: number) => {
    if (!window.confirm(t('confirmDeleteWorkReport'))) return;

    try {
      const backendUrl = sessionStorage.getItem('backendUrl') || 'http://localhost:1337';
      await fetch(`${backendUrl}/api/work-reports/${reportId}`, {
        method: 'DELETE',
        headers: authHeaders,
      });
      
      await fetchWorkReports();
    } catch (error) {
      console.error('Error deleting work report:', error);
    }
  };

  const handleFormSave = async () => {
    setShowForm(false);
    setEditingReport(null);
    await fetchWorkReports();
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingReport(null);
  };

  const handleViewReport = (report: WorkReport) => {
    setSelectedReport(report);
    setShowReportView(true);
  };

  const handleEditFromView = () => {
    setShowReportView(false);
    setEditingReport(selectedReport);
    setShowForm(true);
  };

  const handleExportPDF = async (report: WorkReport) => {
    try {
      const backendUrl = sessionStorage.getItem('backendUrl') || 'http://localhost:1337';
      const response = await fetch(`${backendUrl}/api/work-reports/${report.id}/export-pdf`, {
        headers: authHeaders,
      });

      if (!response.ok) {
        throw new Error(t('exportPdfError'));
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Parte_${report.attributes.numero}_${report.attributes.fecha}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert(t('exportPdfError'));
    }
  };

  const handleExportExcel = async (report: WorkReport) => {
    try {
      const backendUrl = sessionStorage.getItem('backendUrl') || 'http://localhost:1337';
      const response = await fetch(`${backendUrl}/api/work-reports/${report.id}/export-excel`, {
        headers: authHeaders,
      });

      if (!response.ok) {
        throw new Error(t('exportExcelError'));
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Parte_${report.attributes.numero}_${report.attributes.fecha}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error exporting Excel:', error);
      alert(t('exportExcelError'));
    }
  };

  const handleCloseView = () => {
    setShowReportView(false);
    setSelectedReport(null);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return '#4CAF50';
      case 'approved': return '#2196F3';
      case 'draft': return '#FF9800';
      default: return '#666';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed': return t('completed');
      case 'approved': return t('approved');
      case 'draft': return t('draft');
      default: return status;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatCurrency = (amount?: number) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('es-ES', { 
      style: 'currency', 
      currency: 'EUR' 
    }).format(amount);
  };

  const filteredReports = workReports.filter(report => {
    const matchesStatus = statusFilter === 'all' || report.attributes.status === statusFilter;
    return matchesStatus;
  });

  if (loading) {
    return <div className="loading">{t('loadingWorkReports')}</div>;
  }

  return (
    <div className="work-reports-list">
      <div className="work-reports-header">
        <h2>{t('workReports')}</h2>
        {canManage && (
          <button 
            onClick={handleCreateReport} 
            className="create-report-btn"
          >
            <AddIcon />
            {t('createWorkReport')}
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="work-reports-filters">
        <select 
          value={statusFilter} 
          onChange={(e) => setStatusFilter(e.target.value)}
          className="filter-select"
        >
          <option value="all">{t('allStatuses')}</option>
          <option value="draft">{t('draft')}</option>
          <option value="completed">{t('completed')}</option>
          <option value="approved">{t('approved')}</option>
        </select>
      </div>

      {/* Reports List */}
      <div className="reports-grid">
        {filteredReports.map((report) => (
          <div key={report.id} className="report-card">
            <div className="report-header">
              <h3>{t('workReportNumber', { number: report.attributes.numero })}</h3>
              <div className="report-actions">
                <button
                  onClick={() => handleViewReport(report)}
                  className="action-btn view-btn"
                  title={t('viewDetails')}
                >
                  <VisibilityIcon />
                </button>
                <button
                  onClick={() => handleExportExcel(report)}
                  className="action-btn excel-btn"
                  title={t('exportExcel')}
                  style={{ color: '#217346' }}
                >
                  <DescriptionIcon />
                </button>
                <button
                  onClick={() => handleExportPDF(report)}
                  className="action-btn pdf-btn"
                  title={t('exportPdf')}
                >
                  <PictureAsPdfIcon />
                </button>
                {canManage && (
                  <>
                    <button
                      onClick={() => handleEditReport(report)}
                      className="action-btn edit-btn"
                      title={t('edit')}
                    >
                      <EditIcon />
                    </button>
                    <button
                      onClick={() => handleDeleteReport(report.id)}
                      className="action-btn delete-btn"
                      title={t('delete')}
                    >
                      <DeleteIcon />
                    </button>
                  </>
                )}
              </div>
            </div>

            <div className="report-meta">
              <span
                className="status-badge"
                style={{ backgroundColor: getStatusColor(report.attributes.status) }}
              >
                {getStatusText(report.attributes.status)}
              </span>
              <span className="date">
                📅 {formatDate(report.attributes.fecha)}
              </span>
              {report.attributes.enviadoPor && (
                <span className="sent-by">
                  👤 {report.attributes.enviadoPor}
                </span>
              )}
            </div>

            {report.attributes.observaciones && (
              <p className="report-description">
                {report.attributes.observaciones.substring(0, 100)}
                {report.attributes.observaciones.length > 100 ? '...' : ''}
              </p>
            )}

            <div className="report-details">
              {report.attributes.personalPropio && report.attributes.personalPropio.length > 0 && (
                <span>👥 {t('employeesCount', { count: report.attributes.personalPropio.length })}</span>
              )}
              {report.attributes.maquinariaPropia && report.attributes.maquinariaPropia.length > 0 && (
                <span>🚜 {t('machineryCount', { count: report.attributes.maquinariaPropia.length })}</span>
              )}
              {report.attributes.materialesExtendido && report.attributes.materialesExtendido.length > 0 && (
                <span>🏗️ {t('materialsCount', { count: report.attributes.materialesExtendido.length })}</span>
              )}
            </div>
          </div>
        ))}
      </div>

      {filteredReports.length === 0 && (
        <div className="no-reports">
          <h3>{t('noWorkReportsFound')}</h3>
          <p>{t('noWorkReportsDescription')}</p>
          {canManage && (
            <button onClick={handleCreateReport} className="create-first-report-btn">
              {t('createFirstWorkReport')}
            </button>
          )}
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <WorkReportFormNew
              projectId={projectId}
              workReport={editingReport}
              onSave={handleFormSave}
              onCancel={handleFormCancel}
            />
          </div>
        </div>
      )}

      {/* Work Report View */}
      {showReportView && selectedReport && (
        <div className="modal-overlay full-screen">
          <div className="modal-content full-screen-content">
            <WorkReportView
              workReport={selectedReport}
              onEdit={canManage ? handleEditFromView : undefined}
              onExportPDF={() => handleExportPDF(selectedReport)}
              onClose={handleCloseView}
            />
          </div>
        </div>
      )}
    </div>
  );
};